import $ from "jquery";
import "select2";

const MODAL_ID = "aa-resource-tags-modal";

function readAdminTagNames() {
  const el = document.getElementById("admin-batch-tag-names");
  if (!el?.textContent?.trim()) return [];
  try {
    const names = JSON.parse(el.textContent);
    return Array.isArray(names) ? names.filter((n) => typeof n === "string" && n.length > 0) : [];
  } catch {
    return [];
  }
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

async function fetchAdminTagNames() {
  const resp = await fetch("/admin/tags/names.json", {
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
    credentials: "same-origin",
  });
  if (!resp.ok) throw new Error(`Failed to load tag names (HTTP ${resp.status})`);
  const data = await resp.json().catch(() => ({}));
  const names = Array.isArray(data) ? data : data?.names;
  return Array.isArray(names) ? names.filter((n) => typeof n === "string" && n.length > 0) : [];
}

function setModalResourceId(modal, resourceId) {
  if (!modal) return;
  modal.dataset.aaResourceId = resourceId ? String(resourceId) : "";
  const debugEl = modal.querySelector("[data-aa-resource-tags-debug]");
  if (debugEl) {
    debugEl.textContent = `Asset ID: ${resourceId ? String(resourceId) : "—"}`;
  }
}

function ensureModal() {
  let modal = document.getElementById(MODAL_ID);
  if (modal) return modal;

  modal = document.createElement("div");
  modal.id = MODAL_ID;
  modal.style.display = "none";
  modal.innerHTML = `
    <div class="aa-batch-add-tags-backdrop" data-aa-resource-tags-backdrop></div>
    <div class="aa-batch-add-tags-dialog" role="document" aria-labelledby="aa-resource-tags-title">
      <div class="aa-batch-add-tags-header">
        <div class="aa-batch-add-tags-title" id="aa-resource-tags-title">Edit resource tags</div>
        <div class="aa-resource-tags-subtitle" data-aa-resource-tags-debug style="margin-top:2px; font-size:12px; color:#6b7280;">
          Asset ID: —
        </div>
        <button type="button" class="aa-batch-add-tags-close" data-aa-resource-tags-close aria-label="Close">×</button>
      </div>
      <div class="aa-batch-add-tags-body">
        <label class="aa-batch-add-tags-label" for="aa-resource-tags-select">Tags</label>
        <div class="aa-batch-add-tags-help">Select from existing tags only.</div>
        <div class="aa-tag-multiselect">
          <select id="aa-resource-tags-select" multiple class="tag-multiselect-select" tabindex="0"></select>
        </div>
        <div class="aa-resource-tags-error" data-aa-resource-tags-error style="display:none; margin-top:8px; color:#b91c1c; font-size:12px;"></div>
      </div>
      <div class="aa-batch-add-tags-footer">
        <button type="button" class="aa-batch-add-tags-cancel" data-aa-resource-tags-cancel>Cancel</button>
        <button type="button" class="aa-batch-add-tags-submit" data-aa-resource-tags-submit>Save</button>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Install ONE set of handlers. Do not capture resourceId in closures.
  if (!modal._aaResourceTagsInstalled) {
    modal._aaResourceTagsInstalled = true;

    const showError = (msg) => {
      const errorEl = modal.querySelector("[data-aa-resource-tags-error]");
      if (!errorEl) return;
      errorEl.textContent = msg || "";
      errorEl.style.display = msg ? "block" : "none";
    };

    const close = () => {
      const select = modal.querySelector("#aa-resource-tags-select");
      destroySelect2(select);
      showError("");
      modal.style.display = "none";
      setModalResourceId(modal, "");
    };

    const submit = async () => {
      showError("");
      const rid = modal.dataset.aaResourceId;
      if (!rid) {
        showError("Missing resource id.");
        return;
      }
      const select = modal.querySelector("#aa-resource-tags-select");
      const tags = readSelected(select);
      try {
        await saveResourceTags(rid, tags);
        close();
      } catch (e) {
        showError(e?.message || "Failed to save tags");
      }
    };

    modal.querySelector("[data-aa-resource-tags-backdrop]")?.addEventListener("click", close);
    modal.querySelector("[data-aa-resource-tags-close]")?.addEventListener("click", close);
    modal.querySelector("[data-aa-resource-tags-cancel]")?.addEventListener("click", close);
    modal.querySelector("[data-aa-resource-tags-submit]")?.addEventListener("click", submit);
  }

  return modal;
}

function destroySelect2(select) {
  if (typeof $ === "undefined" || !$.fn?.select2) return;
  const $s = $(select);
  if ($s.data("select2")) $s.select2("destroy");
}

function populate(select, names, selected) {
  select.innerHTML = "";
  const selectedSet = new Set(selected);
  for (const name of names) {
    const opt = document.createElement("option");
    opt.value = name;
    opt.textContent = name;
    opt.selected = selectedSet.has(name);
    select.appendChild(opt);
  }
}

function initSelect2(select, modal) {
  if (typeof $ === "undefined" || !$.fn?.select2) return;
  $(select).select2({
    placeholder: "Select tags...",
    allowClear: true,
    width: "100%",
    dropdownParent: $(modal),
  });
}

function readSelected(select) {
  if (typeof $ !== "undefined" && $.fn?.select2 && $(select).data("select2")) {
    const v = $(select).val();
    return Array.isArray(v) ? v : v ? [v] : [];
  }
  return Array.from(select.selectedOptions).map((o) => o.value);
}

async function fetchResourceTags(resourceId) {
  // eslint-disable-next-line no-console
  console.log('[resource_tags] fetchResourceTags', { resourceId });
  const resp = await fetch(`/admin/resources/${resourceId}/tags.json`, {
    headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() },
    credentials: "same-origin",
  });
  if (!resp.ok) throw new Error(`Failed to load tags (HTTP ${resp.status})`);
  const data = await resp.json();
  return Array.isArray(data.tags) ? data.tags : [];
}

async function saveResourceTags(resourceId, tags) {
  // eslint-disable-next-line no-console
  console.log('[resource_tags] saveResourceTags', { resourceId, tags });
  const resp = await fetch(`/admin/resources/${resourceId}/update_tags`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-CSRF-Token": csrfToken(),
    },
    credentials: "same-origin",
    body: JSON.stringify({ tag_list: tags }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || `Failed to save tags (HTTP ${resp.status})`);
  return data;
}

async function openResourceTagsModal({ resourceId }) {
  if (!resourceId) return;
  // eslint-disable-next-line no-console
  console.log('[resource_tags] openResourceTagsModal', { resourceId });

  const modal = ensureModal();
  const select = modal.querySelector("#aa-resource-tags-select");
  const errorEl = modal.querySelector("[data-aa-resource-tags-error]");

  const showError = (msg) => {
    if (!errorEl) return;
    errorEl.textContent = msg || "";
    errorEl.style.display = msg ? "block" : "none";
  };

  setModalResourceId(modal, resourceId);

  showError("");

  let names = [];
  try {
    names = await fetchAdminTagNames();
  } catch {
    names = readAdminTagNames();
  }
  destroySelect2(select);
  populate(select, names, []);
  initSelect2(select, modal);

  modal.style.display = "block";

  try {
    const selected = await fetchResourceTags(resourceId);
    destroySelect2(select);
    populate(select, names, selected);
    initSelect2(select, modal);
  } catch (e) {
    showError(e?.message || "Failed to load tags");
  }
}

function install() {
  // eslint-disable-next-line no-console
  console.log('[resource_tags] install resource tags modal listeners');
  window.addEventListener("admin:resource:editTags", (event) => {
    // eslint-disable-next-line no-console
    console.log('[resource_tags] received admin:resource:editTags', { resourceId: event.detail?.resourceId });
    openResourceTagsModal({ resourceId: event.detail?.resourceId });
  });
}

// Allow React toolbar to open this reliably (no timing issues).
window.AAOpenResourceTagsModal = (resourceId) => openResourceTagsModal({ resourceId });
// eslint-disable-next-line no-console
console.log('[resource_tags] AAOpenResourceTagsModal attached', { readyState: document.readyState });

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", install);
} else {
  install();
}

export default {};

